import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { List, X } from '@phosphor-icons/react';
import { useLanguage } from '../../context/LanguageContext';
import { useT } from '../../hooks/useTranslation';
import { BOOKING_DISABLED } from '../../config/maintenance';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { lang, setLang } = useLanguage();
  const t = useT();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.nav.activities, hash: '#activities' },
    { name: t.nav.gallery, hash: '#gallery' },
    { name: t.nav.reviews, hash: '#reviews' },
    { name: t.nav.location, hash: '#location' },
    { name: t.nav.houseRules, hash: '#hazirend' },
    { name: t.nav.contact, hash: '#contact' },
  ];

  const scrollToSection = useCallback((e: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  }, []);

  const toggleLang = () => setLang(lang === 'hu' ? 'en' : 'hu');

  const textColor = isScrolled ? 'text-forest' : 'text-sand';
  const hoverColor = isScrolled ? 'hover:text-timber' : 'hover:text-gold';
  const underlineColor = isScrolled ? 'bg-timber' : 'bg-gold';

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        BOOKING_DISABLED ? 'top-[44px] md:top-[40px]' : 'top-0'
      } ${
        isScrolled ? 'bg-sand/90 backdrop-blur-md py-4 shadow-sm' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
        <div className="nav-fade-in" style={{ animationDelay: '0.2s' }}>
          <Link to="/" className="z-50 relative block w-24 h-8" aria-label="haaziko főoldal">
            <img
              src="/images/navbar_vilagos.webp"
              alt="haaziko."
              width="96"
              height="96"
              className={`h-24 w-auto absolute top-1/2 -translate-y-1/2 left-0 transition-opacity duration-300 ${isScrolled ? 'opacity-0' : 'opacity-100'}`}
            />
            <img
              src="/images/navbar_sotet.webp"
              alt="haaziko."
              width="96"
              height="96"
              className={`h-24 w-auto absolute top-1/2 -translate-y-1/2 left-0 transition-opacity duration-300 ${isScrolled ? 'opacity-100' : 'opacity-0'}`}
            />
          </Link>
        </div>

        <div className="hidden md:flex items-center space-x-8 lg:space-x-10 nav-fade-in" style={{ animationDelay: '0.4s' }}>
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.hash}
              onClick={(e) => scrollToSection(e, link.hash)}
              className={`text-xs tracking-widest uppercase transition-colors group relative ${textColor} ${hoverColor}`}
            >
              {link.name}
              <span className={`absolute -bottom-1 left-0 w-0 h-px transition-all duration-300 group-hover:w-full ${underlineColor}`} />
            </a>
          ))}

          <button
            onClick={toggleLang}
            className={`text-xs tracking-widest uppercase transition-colors ${textColor} ${hoverColor} flex items-center gap-1.5`}
            // Nincs aria-label: a felolvasott név így PONTOSAN a látható felirat („HU | EN”).
            // Egy eltérő aria-label megsérti a WCAG 2.5.3-at (Label in Name).
            title={lang === 'hu' ? 'Váltás angol nyelvre' : 'Switch to Hungarian'}
          >
            <span className={lang === 'hu' ? 'opacity-100 font-medium' : 'opacity-40'}>HU</span>
            <span className="opacity-30">|</span>
            <span className={lang === 'en' ? 'opacity-100 font-medium' : 'opacity-40'}>EN</span>
          </button>

          <a
            href="#booking"
            onClick={(e) => scrollToSection(e, '#booking')}
            className={`px-6 py-3 text-xs uppercase tracking-widest transition-colors ${
              isScrolled ? 'bg-forest text-sand hover:bg-forest/90' : 'bg-sand text-forest hover:bg-gold'
            }`}
          >
            {t.nav.booking}
          </a>
        </div>

        <button
          className={`md:hidden z-50 transition-colors nav-fade-in ${isScrolled ? 'text-forest' : 'text-sand'}`}
          style={{ animationDelay: '0.4s' }}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? 'Menü bezárása' : 'Menü megnyitása'}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X size={28} /> : <List size={28} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-sand z-40 flex flex-col items-center justify-center space-y-7 menu-in">
          {navLinks.map((link, i) => (
            <a
              key={link.name}
              href={link.hash}
              onClick={(e) => scrollToSection(e, link.hash)}
              className="text-2xl font-serif text-forest hover:text-timber transition-colors menu-item-in"
              style={{ animationDelay: `${0.1 + i * 0.06}s` }}
            >
              {link.name}
            </a>
          ))}
          <a
            href="#booking"
            onClick={(e) => scrollToSection(e, '#booking')}
            className="px-8 py-4 mt-2 bg-forest text-sand text-lg uppercase tracking-widest hover:bg-timber transition-colors menu-item-in"
            style={{ animationDelay: `${0.1 + navLinks.length * 0.06}s` }}
          >
            {t.nav.booking}
          </a>
          <button
            onClick={toggleLang}
            className="text-sm uppercase tracking-widest text-charcoal/50 hover:text-forest transition-colors flex items-center gap-2 menu-item-in"
            style={{ animationDelay: '0.5s' }}
          >
            <span className={lang === 'hu' ? 'text-forest font-medium' : ''}>HU</span>
            <span className="opacity-30">|</span>
            <span className={lang === 'en' ? 'text-forest font-medium' : ''}>EN</span>
          </button>
        </div>
      )}
    </nav>
  );
};
